import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import type { Org } from "shared";

import { db } from "../firebase/clientApp";

const TEST_ORG_COOKIE_NAME = "ohcTestOrg";
const TEST_ORG_QUERY_PARAM = "ohcTestOrg";

/**
 * Fetch organization by domain
 */
export async function fetchOrgByDomain(
  domain: string,
): Promise<{ org: Org; orgUid: string } | null> {
  const orgsRef = collection(db, "orgs");
  const q = query(orgsRef, where("domains", "array-contains", domain));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return null;
  }

  if (querySnapshot.size > 1) {
    console.warn(
      `Multiple organizations found for domain ${domain}, using first match`,
    );
  }

  const orgDoc = querySnapshot.docs[0];
  const orgData = orgDoc.data() as Org;

  if (!orgData.active) {
    throw new Error(
      `Organization "${orgData.name}" is not active. Please contact support.`,
    );
  }

  return {
    org: orgData,
    orgUid: orgDoc.id,
  };
}

/**
 * Fetch organization by explicit org UID
 */
export async function fetchOrgByUid(
  orgUid: string,
): Promise<{ org: Org; orgUid: string } | null> {
  const orgRef = doc(db, "orgs", orgUid);
  const snapshot = await getDoc(orgRef);

  if (!snapshot.exists()) {
    return null;
  }

  const orgData = snapshot.data() as Org;

  if (!orgData.active) {
    throw new Error(
      `Organization "${orgData.name}" is not active. Please contact support.`,
    );
  }

  return {
    org: orgData,
    orgUid: snapshot.id,
  };
}

function getTestOrgOverrideFromCookie(): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const cookies = document.cookie?.split(";") ?? [];
  for (const cookie of cookies) {
    const [rawName, ...rawValue] = cookie.trim().split("=");
    if (rawName === TEST_ORG_COOKIE_NAME) {
      const value = rawValue.join("=");
      if (value && /^[A-Za-z0-9_-]{1,128}$/.test(value)) {
        return value;
      }
      console.warn(
        `[fetchCurrentOrg] Ignoring invalid override cookie value: "${value}"`,
      );
    }
  }

  return null;
}

function getTestOrgOverrideFromQuery(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const value = params.get(TEST_ORG_QUERY_PARAM);

  if (!value) {
    return null;
  }

  if (!/^[A-Za-z0-9_-]{1,128}$/.test(value)) {
    console.warn(
      `[fetchCurrentOrg] Ignoring invalid override query value: "${value}"`,
    );
    return null;
  }

  return value;
}

/**
 * Fetch organization for the current browser hostname or test override
 */
export async function fetchCurrentOrg(): Promise<{
  org: Org;
  orgUid: string;
} | null> {
  if (typeof window === "undefined") {
    throw new Error(
      "fetchCurrentOrg can only be called in browser environment",
    );
  }

  const queryOverride = getTestOrgOverrideFromQuery();
  if (queryOverride) {
    const overrideOrg = await fetchOrgByUid(queryOverride);
    if (overrideOrg) {
      document.cookie = `${TEST_ORG_COOKIE_NAME}=${queryOverride}; path=/; SameSite=Lax`;
      return overrideOrg;
    }
    console.warn(
      `[fetchCurrentOrg] Override query specified "${queryOverride}" but no such org exists; falling back to cookie/hostname`,
    );
  }

  const overrideOrgUid = getTestOrgOverrideFromCookie();
  if (overrideOrgUid) {
    const overrideOrg = await fetchOrgByUid(overrideOrgUid);
    if (overrideOrg) {
      return overrideOrg;
    }
    console.warn(
      `[fetchCurrentOrg] Override cookie specified "${overrideOrgUid}" but no such org exists; falling back to hostname`,
    );
  }

  return fetchOrgByDomain(window.location.hostname);
}

/**
 * Update organization timezone
 *
 * @param orgUid - Organization UID
 * @param timezone - Timezone string (e.g., "America/New_York")
 * @returns Promise that resolves when update is complete
 *
 * @example
 * await updateOrgTimezone('org-123', 'America/Chicago');
 */
export async function updateOrgTimezone(
  orgUid: string,
  timezone: string,
): Promise<void> {
  const orgRef = doc(db, "orgs", orgUid);

  // Validate that the org exists
  const snapshot = await getDoc(orgRef);
  if (!snapshot.exists()) {
    throw new Error(`Organization with UID "${orgUid}" not found`);
  }

  // Update the timezone in metadata
  await updateDoc(orgRef, {
    "metadata.timezone": timezone,
  });
}

/**
 * Update organization suggested tags for calendar events
 *
 * @param orgUid - Organization UID
 * @param tags - Array of suggested tag strings
 * @returns Promise that resolves when update is complete
 *
 * @example
 * await updateOrgSuggestedTags('org-123', ['meeting', 'camping', 'service']);
 */
export async function updateOrgSuggestedTags(
  orgUid: string,
  tags: string[],
): Promise<void> {
  const orgRef = doc(db, "orgs", orgUid);

  // Validate that the org exists
  const snapshot = await getDoc(orgRef);
  if (!snapshot.exists()) {
    throw new Error(`Organization with UID "${orgUid}" not found`);
  }

  // Update the suggested tags in calendar settings
  await updateDoc(orgRef, {
    "metadata.calendarSettings.suggestedTags": tags,
  });
}
